import { useState } from 'react';
import { BookOpen, Plus } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, EmptyState, Input, Skeleton, Textarea, useConfirm } from '@techsofit/ui';
import { FormShell } from '../components/FormShell';
import { ImageUpload } from '../components/ImageUpload';
import { useAsync } from '../lib/useAsync';
import { createArticle, deleteArticle, getArticles, updateArticle, type AdminArticle } from '../lib/adminApi';

export function LearnPage() {
  const { data, loading, error, reload } = useAsync(getArticles);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const list = data ?? [];

  if (adding) {
    return (
      <FormShell title="New article" onBack={() => setAdding(false)}>
        <ArticleForm mode="create" onCancel={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />
      </FormShell>
    );
  }
  const editing = list.find((a) => a.id === editingId);
  if (editing) {
    return (
      <FormShell title="Edit article" onBack={() => setEditingId(null)}>
        <ArticleForm mode="edit" article={editing} onCancel={() => setEditingId(null)} onDone={() => { setEditingId(null); reload(); }} />
      </FormShell>
    );
  }

  return (
    <section className="mt-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-display-md text-ink">Learn</h1>
          <p className="mt-1 text-ink-soft">Articles and guides shown in the app.</p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden /> New article
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 flex flex-col gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : error ? (
        <EmptyState title="Couldn't load articles" description={error} />
      ) : list.length === 0 ? (
        <EmptyState title="No articles yet" description="Write your first guide for the app." />
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {list.map((a) => (
            <ArticleCard key={a.id} a={a} onEdit={() => setEditingId(a.id)} onDeleted={reload} />
          ))}
        </div>
      )}
    </section>
  );
}

function ArticleCard({ a, onEdit, onDeleted }: { a: AdminArticle; onEdit: () => void; onDeleted: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  async function remove() {
    if (!(await confirm({ title: `Delete "${a.title}"?`, description: 'This cannot be undone.', confirmLabel: 'Delete', tone: 'danger' }))) return;
    setBusy(true);
    try {
      await deleteArticle(a.id);
      onDeleted();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="flex items-center gap-3 p-4">
      <span className="grid size-10 shrink-0 place-items-center rounded-control bg-surface text-ink">
        <BookOpen className="size-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-ink">{a.title}</p>
          {!a.published && <span className="rounded-pill bg-surface px-2 py-0.5 text-xs text-muted">Draft</span>}
          {a.featured && <span className="rounded-pill bg-accent-soft px-2 py-0.5 text-xs text-ink">Featured</span>}
        </div>
        <p className="text-xs text-muted">{a.category ?? '—'} · {a.readMinutes ?? '?'} min</p>
      </div>
      <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
      <Button size="sm" variant="ghost" loading={busy} onClick={remove} className="text-bad-ink">Delete</Button>
    </Card>
  );
}

function ArticleForm({ mode, article, onCancel, onDone }: { mode: 'create' | 'edit'; article?: AdminArticle; onCancel: () => void; onDone: () => void }) {
  const [slug, setSlug] = useState(article?.slug ?? '');
  const [title, setTitle] = useState(article?.title ?? '');
  const [category, setCategory] = useState(article?.category ?? '');
  const [readMinutes, setReadMinutes] = useState(article?.readMinutes ? String(article.readMinutes) : '');
  const [excerpt, setExcerpt] = useState(article?.excerpt ?? '');
  const [body, setBody] = useState(article?.body ?? '');
  const [coverImage, setCoverImage] = useState(article?.coverImage ?? '');
  const [published, setPublished] = useState(article?.published ?? false);
  const [featured, setFeatured] = useState(article?.featured ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (!title.trim()) return setError('Give the article a title.');
    if (mode === 'create' && !slug.trim()) return setError('Give the article a slug (e.g. rest-days-matter).');
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        category: category.trim() || null,
        readMinutes: readMinutes ? Number(readMinutes) : null,
        excerpt: excerpt.trim() || null,
        body,
        coverImage: coverImage.trim() || null,
        published,
        featured,
      };
      if (mode === 'create') await createArticle({ ...payload, slug: slug.trim() });
      else await updateArticle(article!.id, payload);
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the article.');
      setSaving(false);
    }
  }

  return (
    <Card className="mb-1 flex flex-col gap-3 p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Why rest days matter" />
        {mode === 'create' && <Input label="Slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="rest-days-matter" />}
        <Input label="Category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Recovery" />
        <Input label="Read minutes" type="number" inputMode="numeric" value={readMinutes} onChange={(e) => setReadMinutes(e.target.value)} />
      </div>
      <ImageUpload value={coverImage || null} onChange={(url) => setCoverImage(url ?? '')} aspect={16 / 9} />
      <Input label="Excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="One-line summary" />
      <Textarea label="Body" value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
      <div className="flex flex-wrap gap-5">
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} /> Published
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} /> Featured
        </label>
      </div>
      {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={submit} loading={saving}>Save</Button>
      </div>
    </Card>
  );
}
